import React, { useEffect } from "react";
import { Form, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import moment from "moment";
import AlertBox from "../../../components/common/AlertBox";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getAllDSRMonthWiseReport } from "../services/reports.services";
import { LoadingPanel } from "../../../components/layout/Loading";
import { clearDSRMonthWiseReportList } from "../reportsSlice";
import ButtonWithLoading from "../../../components/common/ButtonWithLoading";
import { formatIndianNumber } from "../../../_helper/helper";
import { sumBy } from "lodash";
import { Typography } from "@progress/kendo-react-common";

const DSRMonthWiseReport: React.FC = () => {
  const dispatch = useAppDispatch();
  const state = JSON.parse(
    sessionStorage.getItem("dsrmonthwisereportState") || "{}"
  );
  const initialValues = JSON.parse(JSON.stringify(state.initialValues));
  const gridRef = React.useRef<any>(null);

  const setPrintStyles = () => {
    const style = document.createElement("style");
    style.type = "text/css";
    style.innerHTML = `
      @media print {
  @page {
    margin-left: 0.5cm;
    margin-right: 0.5cm;
  }
  @page landscapePage {
    size: landscape;
  }
  .salesmanwisereport-print-break {
    page: landscapePage;
    page-break-before: always;
  }
}
    `;
    document.head.appendChild(style);
  };

  const DSRMonthWiseReportList = useAppSelector(
    (state) => state.reports.DSRMonthWiseReportList
  );
  const loading = useAppSelector((state) => state.reports.loading);

  const fetchReport = async () => {
    const payload = {
      sr_id: state?.sr_id,
      from_date: state?.from_date,
      to_date: state?.to_date,
    };
    const res = await dispatch(getAllDSRMonthWiseReport(payload));
    if (res.meta.requestStatus === "fulfilled") {
      setPrintStyles();
      setTimeout(() => {
        window.print();
      }, 2000);
    }
  };

  useEffect(() => {
    const handleTabClose = () => {
      sessionStorage.removeItem("dsrmonthwisereportState");
    };
    fetchReport();
    return () => {
      window.removeEventListener("unload", handleTabClose);
      dispatch(clearDSRMonthWiseReportList());
    };
  }, []);

  const checkInclude = (key: string, formRenderProps: FormRenderProps) => {
    return formRenderProps.valueGetter("filter_columns")?.includes(key);
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {DSRMonthWiseReportList && DSRMonthWiseReportList.length ? (
        <Form
          initialValues={initialValues}
          render={(formRenderProps: FormRenderProps) => {
            return (
              <FormElement style={{ width: "100%" }}>
                <GridLayout
                  //   style={{ marginRight: 30 }}
                  gap={{ rows: 10, cols: 10 }}
                  cols={[{ width: "100%" }]}
                >
                  <GridLayoutItem>
                    <ButtonWithLoading
                      buttonStyle={{
                        position: "fixed",
                        right: 20,
                        top: 20,
                        zIndex: 100,
                      }}
                      label={"Print"}
                      className="no-visible-print-btn"
                      type="button"
                      onClick={() => window.print()}
                    />

                    <Typography.h5 style={{ textAlign: "center", margin: "0" }}>
                      SR Monthly Report
                    </Typography.h5>
                    <Typography.p style={{ textAlign: "center", margin: "0" }}>
                      For Period :{" "}
                      {state.from_date &&
                        state.to_date &&
                        `( ${moment(state.from_date, "YYYY-MM-DD").format(
                          "DD/MM/YYYY"
                        )} - ${moment(state.to_date, "YYYY-MM-DD").format(
                          "DD/MM/YYYY"
                        )} )`}
                    </Typography.p>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <table
                      cellSpacing={1}
                      cellPadding={2}
                      className={`table table-bordered registerreport-print`}
                    >
                      <thead>
                        <tr
                          className="text-center fw-600"
                          style={{
                            fontSize: 12,
                          }}
                        >
                          <th>SR. No</th>
                          {checkInclude("sr_name", formRenderProps) && (
                            <th style={{ minWidth: 150 }}>
                              Sales Representaive
                            </th>
                          )}
                          {checkInclude("month", formRenderProps) && (
                            <th>Month</th>
                          )}
                          {checkInclude("plan_date", formRenderProps) && (
                            <th style={{ minWidth: 110 }}>Plan Date</th>
                          )}
                          {checkInclude("day", formRenderProps) && <th>Day</th>}
                          {checkInclude("working_day", formRenderProps) && (
                            <th>W.Day</th>
                          )}
                          {checkInclude("holiday", formRenderProps) && (
                            <th>Holiday</th>
                          )}
                          {checkInclude(
                            "distributor_name",
                            formRenderProps
                          ) && (
                            <th style={{ minWidth: 150 }}>Distributor Name</th>
                          )}
                          {checkInclude("area_name", formRenderProps) && (
                            <th>Town</th>
                          )}
                          {checkInclude("total_call", formRenderProps) && (
                            <th>TC</th>
                          )}
                          {checkInclude("prod_call", formRenderProps) && (
                            <th>PC</th>
                          )}
                          {checkInclude(
                            "prod_call_percentage",
                            formRenderProps
                          ) && <th>%</th>}
                          {checkInclude("da_local", formRenderProps) && (
                            <th>DA-Local</th>
                          )}
                          {checkInclude("da_up_nh", formRenderProps) && (
                            <th>DA-UP / NH</th>
                          )}
                          {checkInclude("other_expense", formRenderProps) && (
                            <th>O Exp</th>
                          )}
                          {checkInclude("total_expense", formRenderProps) && (
                            <th>Total Exp</th>
                          )}
                          {checkInclude("CTCC_amount", formRenderProps) && (
                            <th>CTCC</th>
                          )}
                          {checkInclude("CTCC_B_amount", formRenderProps) && (
                            <th>CTCC-B</th>
                          )}
                          {checkInclude("pm_amount", formRenderProps) && (
                            <th>PM</th>
                          )}
                          {checkInclude("pm_b_amount", formRenderProps) && (
                            <th>PM-B</th>
                          )}
                          {checkInclude("pickle", formRenderProps) && (
                            <th>Pickle</th>
                          )}
                          {checkInclude("hing", formRenderProps) && (
                            <th>Hing</th>
                          )}
                          {checkInclude("instant", formRenderProps) && (
                            <th>Instant</th>
                          )}
                          {checkInclude("flour", formRenderProps) && (
                            <th>Flour</th>
                          )}
                          {checkInclude("papad_mathiya", formRenderProps) && (
                            <th>Papad / Mathiya</th>
                          )}
                          {checkInclude("other_l", formRenderProps) && (
                            <th>Other-L</th>
                          )}
                          {checkInclude("sale_in_amount", formRenderProps) && (
                            <th>Sales in Amount</th>
                          )}
                          {checkInclude("sale_in_kg", formRenderProps) && (
                            <th>Sales in KG</th>
                          )}
                          {checkInclude(
                            "sales_percentage",
                            formRenderProps
                          ) && <th>Sales (%)</th>}
                        </tr>
                      </thead>

                      {/* Table Body */}

                      <tbody>
                        {DSRMonthWiseReportList.map(
                          (item: any, rowIndex: number) => (
                            <>
                              <tr key={rowIndex}>
                                <td>{rowIndex + 1}</td>
                                {checkInclude("sr_name", formRenderProps) && (
                                  <td>{item?.sr_name}</td>
                                )}
                                {checkInclude("month", formRenderProps) && (
                                  <td>{item?.month}</td>
                                )}
                                {checkInclude("plan_date", formRenderProps) && (
                                  <td>{item?.plan_date}</td>
                                )}
                                {checkInclude("day", formRenderProps) && (
                                  <td>{item?.day}</td>
                                )}

                                {checkInclude(
                                  "working_day",
                                  formRenderProps
                                ) && <td>{item?.working_day}</td>}
                                {checkInclude("holiday", formRenderProps) && (
                                  <td>{item?.holiday}</td>
                                )}
                                {checkInclude(
                                  "distributor_name",
                                  formRenderProps
                                ) && <td>{item?.distributor_name}</td>}
                                {checkInclude("area_name", formRenderProps) && (
                                  <td>{item?.area_name}</td>
                                )}
                                {checkInclude(
                                  "total_call",
                                  formRenderProps
                                ) && (
                                  <td className="text-center">
                                    {item?.total_call}
                                  </td>
                                )}
                                {checkInclude("prod_call", formRenderProps) && (
                                  <td className="text-center">
                                    {item?.prod_call}
                                  </td>
                                )}
                                {checkInclude(
                                  "prod_call_percentage",
                                  formRenderProps
                                ) && (
                                  <td className="text-center">
                                    {item?.prod_call_percentage}
                                  </td>
                                )}
                                {checkInclude("da_local", formRenderProps) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.da_local)}
                                  </td>
                                )}
                                {checkInclude("da_up_nh", formRenderProps) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.da_up_nh)}
                                  </td>
                                )}
                                {checkInclude(
                                  "other_expense",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.other_expense)}
                                  </td>
                                )}
                                {checkInclude(
                                  "total_expense",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.total_expense)}
                                  </td>
                                )}
                                {checkInclude(
                                  "CTCC_amount",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.CTCC_amount)}
                                  </td>
                                )}
                                {checkInclude(
                                  "CTCC_B_amount",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.CTCC_B_amount)}
                                  </td>
                                )}
                                {checkInclude("pm_amount", formRenderProps) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.pm_amount)}
                                  </td>
                                )}
                                {checkInclude(
                                  "pm_b_amount",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.pm_b_amount)}
                                  </td>
                                )}
                                {checkInclude("pickle", formRenderProps) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.pickle)}
                                  </td>
                                )}
                                {checkInclude("hing", formRenderProps) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.hing)}
                                  </td>
                                )}
                                {checkInclude("instant", formRenderProps) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.instant)}
                                  </td>
                                )}
                                {checkInclude("flour", formRenderProps) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.flour)}
                                  </td>
                                )}
                                {checkInclude(
                                  "papad_mathiya",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.papad_mathiya)}
                                  </td>
                                )}
                                {checkInclude("other_l", formRenderProps) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.other_l)}
                                  </td>
                                )}
                                {checkInclude(
                                  "sale_in_amount",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {formatIndianNumber(item?.sale_in_amount)}
                                  </td>
                                )}
                                {checkInclude(
                                  "sale_in_kg",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {item?.sale_in_kg?.toFixed(2)}
                                  </td>
                                )}
                                {checkInclude(
                                  "sales_percentage",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {item?.sales_percentage}
                                  </td>
                                )}
                              </tr>
                            </>
                          )
                        )}

                        <tr>
                          <td>TOTAL</td>
                          {checkInclude("sr_name", formRenderProps) && (
                            <td></td>
                          )}
                          {checkInclude("month", formRenderProps) && <td></td>}
                          {checkInclude("plan_date", formRenderProps) && (
                            <td></td>
                          )}
                          {checkInclude("day", formRenderProps) && <td></td>}

                          {checkInclude("working_day", formRenderProps) && (
                            <td className="text-center">
                              {sumBy(DSRMonthWiseReportList, "working_day")}
                            </td>
                          )}
                          {checkInclude("holiday", formRenderProps) && (
                            <td></td>
                          )}
                          {checkInclude(
                            "distributor_name",
                            formRenderProps
                          ) && <td></td>}
                          {checkInclude("area_name", formRenderProps) && (
                            <td></td>
                          )}
                          {checkInclude("total_call", formRenderProps) && (
                            <td className="text-center">
                              {sumBy(DSRMonthWiseReportList, "total_call")}
                            </td>
                          )}
                          {checkInclude("prod_call", formRenderProps) && (
                            <td className="text-center">
                              {sumBy(DSRMonthWiseReportList, "prod_call")}
                            </td>
                          )}
                          {checkInclude(
                            "prod_call_percentage",
                            formRenderProps
                          ) && <td className="text-center">0</td>}
                          {checkInclude("da_local", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "da_local")
                              )}
                            </td>
                          )}
                          {checkInclude("da_up_nh", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "da_up_nh")
                              )}
                            </td>
                          )}
                          {checkInclude("other_expense", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "other_expense")
                              )}
                            </td>
                          )}
                          {checkInclude("total_expense", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "total_expense")
                              )}
                            </td>
                          )}
                          {checkInclude("CTCC_amount", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "CTCC_amount")
                              )}
                            </td>
                          )}
                          {checkInclude("CTCC_B_amount", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "CTCC_B_amount")
                              )}
                            </td>
                          )}
                          {checkInclude("pm_amount", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "pm_amount")
                              )}
                            </td>
                          )}
                          {checkInclude("pm_b_amount", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "pm_b_amount")
                              )}
                            </td>
                          )}
                          {checkInclude("pickle", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "pickle")
                              )}
                            </td>
                          )}
                          {checkInclude("hing", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "hing")
                              )}
                            </td>
                          )}
                          {checkInclude("instant", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "instant")
                              )}
                            </td>
                          )}
                          {checkInclude("flour", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "flour")
                              )}
                            </td>
                          )}
                          {checkInclude("papad_mathiya", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "papad_mathiya")
                              )}
                            </td>
                          )}
                          {checkInclude("other_l", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "other_l")
                              )}
                            </td>
                          )}
                          {checkInclude("sale_in_amount", formRenderProps) && (
                            <td className="text-end">
                              {formatIndianNumber(
                                sumBy(DSRMonthWiseReportList, "sale_in_amount")
                              )}
                            </td>
                          )}
                          {checkInclude("sale_in_kg", formRenderProps) && (
                            <td className="text-end">
                              {sumBy(
                                DSRMonthWiseReportList,
                                "sale_in_kg"
                              )?.toFixed(2)}
                            </td>
                          )}
                          {checkInclude(
                            "sales_percentage",
                            formRenderProps
                          ) && (
                            <td className="text-end">
                              {/* {sumBy(
                                DSRMonthWiseReportList,
                                "sales_percentage"
                              )} */}
                              0
                            </td>
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </GridLayoutItem>
                </GridLayout>
              </FormElement>
            );
          }}
        />
      ) : (
        <AlertBox />
      )}
    </>
  );
};

export default DSRMonthWiseReport;
